import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { printersQuery, type DevicePrintersPage, type PrinterCard, type SpecPair } from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Printers a fisei - inventoree/devices/others/devicePrinters.php cu
 * gestul din device_workspace.js: Physical (USB / LPT / incorporat) si
 * Network (IP / WSD / IPP / share), un patrat pe coada cu chip-urile de
 * Default / Offline / Stopped / Supplies / Problem si specificatiile sub
 * chevron. PDF / XPS / Fax nu ajung aici.
 */

export function DevicePrinters({ id }: ScreenProps) {
  const { data, error } = useQuery(printersQuery(id))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return <Printers page={data} />
}

function Printers({ page }: { page: DevicePrintersPage }) {
  if (page.empty) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle"></i> No printers are configured on this machine.
      </div>
    )
  }

  return (
    <>
      <style>{'.dcf-chip-default { background: #ece9fb; color: #5a48c9; }'}</style>
      <div className="device-printers">
        {page.physical.length > 0 && (
          <section className="dpr-block">
            <h3 className="dpr-heading">Physical</h3>
            <p className="dpr-lead">Plugged into this machine — USB, parallel, or built-in.</p>
            <div className="device-config device-attached">
              {page.physical.map((card, i) => (
                <Card key={`p-${card.title}-${i}`} card={card} />
              ))}
            </div>
          </section>
        )}
        {page.network.length > 0 && (
          <section className="dpr-block">
            <h3 className="dpr-heading">Network</h3>
            <p className="dpr-lead">Reached over IP, WSD, IPP, or a shared queue.</p>
            <div className="device-config device-attached">
              {page.network.map((card, i) => (
                <Card key={`n-${card.title}-${i}`} card={card} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  )
}

function Card({ card }: { card: PrinterCard }) {
  const [open, setOpen] = useState(false)

  return (
    <section className={`dcf-card${card.hasProblem ? ' has-problem' : ''}${open ? ' is-open' : ''}`}>
      <header className="dcf-head" onClick={() => setOpen(!open)}>
        <span className="dcf-icon">
          <i className="fas fa-print" aria-hidden="true"></i>
        </span>
        <span className="dcf-title">{card.title}</span>
        <span className="dcf-chip">{card.conn}</span>
        {card.isDefault && (
          <span className="dcf-chip dcf-chip-default" title="The default printer on this machine">
            Default
          </span>
        )}
        {card.stopped && <span className="dcf-chip dcf-chip-warn">{card.stoppedLabel}</span>}
        {card.supplies !== '' && <span className="dcf-chip dcf-chip-warn">{card.supplies}</span>}
        {card.hasProblem && <span className="dcf-chip dcf-chip-warn">Problem</span>}
        <i className="fas fa-chevron-down dcf-chevron" aria-hidden="true"></i>
      </header>

      <div className="dcf-summary">
        {card.where !== '' && (
          <div className="dcf-line" title={card.where}>
            {card.where}
          </div>
        )}
        {card.location !== '' && (
          <div className="dcf-line dcf-muted">
            <i className="fas fa-map-marker-alt" aria-hidden="true"></i> {card.location}
          </div>
        )}
        {card.driver !== '' && <div className="dcf-line dcf-muted">{card.driver}</div>}
        {card.detail !== '' && <div className="dcf-line dcf-warn">{card.detail}</div>}
      </div>

      <div className="dcf-details">
        <dl className="dcf-specs">
          {card.specs.map((p, i) => (
            <Pair key={i} pair={p} />
          ))}
        </dl>
      </div>
    </section>
  )
}

function Pair({ pair }: { pair: SpecPair }) {
  return (
    <>
      <dt>{pair.name}</dt>
      <dd>
        {pair.mark === 'dash' ? <span className="dcf-muted">&ndash;</span> : pair.mark === 'na' ? <em className="color-steel">n/a</em> : pair.value}
      </dd>
    </>
  )
}
